import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('[name="website_alt"]');
    if (honeypot && honeypot.value.trim()) {
      setSubmitted(true);
      return;
    }
    setSubmitted(true);
    setFormError('');
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="container-main">
          <div className="max-w-2xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
              <a href="/" className="hover:text-primary-500 transition-colors">{t('nav.home')}</a>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-700">{t('contact.title')}</span>
            </nav>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight mb-4">
              {t('contact.contactUs')}
            </h1>
            <p className="text-sm text-foreground-600 mb-8">
              {t('contact.description')}
            </p>

            {submitted ? (
              <div className="p-6 rounded-xl bg-accent-50 border border-accent-200/70 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-check-line text-accent-500 text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-1">{t('contact.messageSent')}</h3>
                <p className="text-sm text-foreground-600">{t('contact.thankYou')}</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-4 px-4 py-2 text-sm text-primary-500 font-medium hover:text-primary-600 transition-colors"
                >
                  {t('contact.sendAnother')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.name')}</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.email')}</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.subject')}</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.message')}</label>
                  <textarea
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300 transition-all resize-none"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">{t('contact.maxChars')}</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                {formError && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}

                <button
                  type="submit"
                  className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors"
                >
                  {t('contact.sendMessage')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}